import { useEffect, useMemo, useState } from 'react';
import { Each } from '../common/Each';
import typo from "../typography.module.css";
import styles from './Table.module.css';
import { ReactComponent as PinIcon } from '../assets/images/icons/ic-pin.svg';
import { formatDateV2, formatTimeV2 } from "../utils"
import Skeleton from './Skeleton';

const formatColumnLabel = (label) => {
    if (typeof label !== 'string') {
        return label;
    }

    const normalized = label.toLocaleLowerCase('it-IT');
    return normalized.replace(/(^|\s)([a-z\u00C0-\u017F])/g, (_, prefix, letter) => `${prefix}${letter.toUpperCase()}`);
}

const Table = ({ loading, columns = [], elements = [], defaultSort = { key: 'id', direction: 'desc' }, onRowClick, style = {}, appearance = 'default' }) => {

    const [currentSort, setCurrentSort] = useState(defaultSort);
    const [sortedElements, setSortedElements] = useState(elements);
    const [_columns, setColumns] = useState(columns);

    useEffect(() => {
        setColumns(columns);
    }, [columns])

    const sortedColumns = useMemo(() => {
        if (!_columns.find(c => c.pinned)) {
            return [..._columns];
        }
        else {
            return [..._columns].sort((a, b) => {
                if (a.pinned && !b.pinned) return -1;
                if (!a.pinned && b.pinned) return 1;
                return 0;
            });
        }
    }, [_columns])

    const sortElements = (key, type) => {
        const direction = currentSort.direction === 'asc' ? 'desc' : 'asc';

        setCurrentSort((prev) => {
            prev.key = key;
            prev.direction = direction;
            return { ...prev }
        });

        const sorted = elements.sort((a, b) => {
            if (direction === 'asc' && type === 'number') {
                return a[key] > b[key] ? 1 : -1;
            } else if (direction === 'desc' && type === 'number') {
                return a[key] < b[key] ? 1 : -1;
            }
            else if (direction === 'asc' && type === 'string') {
                return (a[key] || '').localeCompare(b[key] || '');
            }
            else if (direction === 'desc' && type === 'string') {
                return (b[key] || '').localeCompare(a[key] || '');
            }
            else if (direction === 'desc' && (type === 'date' || type === 'datetime')) {
                return new Date(b[key]) - new Date(a[key]);
            }
            else if (direction === 'asc' && (type === 'date' || type === 'datetime')) {
                return new Date(a[key]) - new Date(b[key]);
            }
            else if (direction === 'desc' && (type === 'custom')) {
                if (a[key].type === 'string' && b[key].type === 'string') {
                    return (b[key].value || '').localeCompare(a[key].value || '');
                }
                else if (a[key].type === 'number' && b[key].type === 'number') {
                    return b[key].value - a[key].value;
                }
            }
            else if (direction === 'asc' && (type === 'custom')) {
                if (a[key].type === 'string' && b[key].type === 'string') {
                    return (a[key].value || '').localeCompare(b[key].value || '');
                }
                else {
                    return a[key].value - b[key].value;
                }
            }
            else {
                return 0;
            }
        });

        setSortedElements(sorted);
    }

    useEffect(() => {
        setSortedElements(elements);
    }, [elements])

    const containerClassName = [
        styles.container,
        appearance === 'minimal' ? styles.minimal : ''
    ].filter(Boolean).join(' ')

    return (
        <div className={containerClassName} style={style}>
            <table>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--background-secondary-color)' }}>
                    <tr>
                        <Each of={sortedColumns} render={(column) => {
                            return (
                                <th className={column.pinned ? styles.fixed : ''}>
                                    <div className={typo.caption} onClick={() => {
                                        sortElements(column.key, column.type)
                                    }}>{formatColumnLabel(column.label)}
                                        {currentSort.key === column.key &&
                                            <>
                                                {currentSort.direction === 'asc' ? '↑' : '↓'}
                                            </>
                                        }
                                    </div>
                                    <PinIcon className={styles.pinIcon} onClick={() => setColumns(_columns.map(c => c.key === column.key ? { ...c, pinned: !c.pinned } : { ...c, pinned: false }))} />
                                </th>
                            )
                        }} />
                    </tr>
                </thead>
                <tbody>
                    <Each of={loading ? Array(25).fill(null) : sortedElements} render={(e) => (
                        <tr onClick={() => {
                            if (typeof onRowClick === 'function') {
                                onRowClick(e)
                            }
                        }}>
                            <Each of={sortedColumns} render={(column) => {

                                let cellStyle = '';
                                switch (column.type) {
                                    case 'string':
                                        cellStyle = styles.string;
                                        break;
                                    case 'number':
                                        cellStyle = styles.number;
                                        break;
                                    case 'date':
                                        cellStyle = styles.date;
                                        break;
                                    case 'custom':
                                        cellStyle = styles.custom;
                                        break;
                                    default:
                                        cellStyle = '';
                                }

                                if (loading) {
                                    return (
                                        <td className={column.pinned ? styles.fixed : ''}>
                                            <a href={"javascript:void(0)"} rel='noreferrer'>
                                                <div className={cellStyle} >
                                                    <Skeleton height={20} width={'100%'} borderRadius={6}/>
                                                </div>
                                            </a>
                                        </td>
                                    )
                                }
                                else {
                                    if (column.type === 'date' && e[column.key]) {
                                        return (
                                            <td className={column.pinned ? styles.fixed : ''}>
                                                <a href={e.href ?? "javascript:void(0)"} rel='noreferrer'>
                                                    <div className={cellStyle} >
                                                        {formatDateV2(e[column.key], { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                    </div>
                                                </a>
                                            </td>
                                        )
                                    }
                                    else if (column.type === 'datetime' && e[column.key]) {
                                        return (
                                            <td className={column.pinned ? styles.fixed : ''}>
                                                <a href={e.href ?? "javascript:void(0)"} rel='noreferrer'>
                                                    <div className={cellStyle} >
                                                        {formatDateV2(e[column.key], { day: '2-digit', month: '2-digit', year: 'numeric' })} - {formatTimeV2(e[column.key])}
                                                    </div>
                                                </a>
                                            </td>
                                        )
                                    }
                                    else if (column.type === 'custom') {
                                        return (
                                            <td className={column.pinned ? styles.fixed : ''}>
                                                <div className={cellStyle} >
                                                    {e[column.key].component ?? e[column.key] ?? 'N/A'}
                                                </div>
                                            </td>
                                        )
                                    }
                                    return (
                                        <td className={column.pinned ? styles.fixed : ''}>
                                            <a href={e.href ?? "javascript:void(0)"} rel='noreferrer'>
                                                <div className={cellStyle} >
                                                    {e[column.key] ?? 'N/A'}
                                                </div>
                                            </a>
                                        </td>
                                    )
                                }
                            }} />
                        </tr>
                    )} />
                </tbody>
            </table>
        </div>
    )

}

export default Table;